import type {
  FinanceChartTrafficSource,
  FinanceMetricKey,
  FinanceMetricsTrafficSource,
} from '@metapic-apps/shared'
import type { GetUserPerformanceSummaryTrafficSource } from '@metapic-apps/shared/src/generated/metapic-api/metapicApi.schemas'
import {
  computed,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  ref,
  toValue,
  watch,
} from 'vue'

import {
  buildChartParams,
  isFinanceMetric,
  normalizePerformanceMetrics,
  toPerformanceChartData,
} from '@/lib/adapters/performance.adapter'
import { useApi } from '@/lib/helpers/api.helpers'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { financeClient } from '@/lib/services/finance.service'
import { metapicApiClient } from '@/lib/services/metapic-api.service'
import { socialMediaClient } from '@/lib/services/social-media.service'
import type {
  AdvertiserMetricKey,
  PerformanceChartData,
  PerformanceChartFilter,
  PerformanceMetricKey,
  PerformanceMetricRaw,
  PerformanceMetrics,
  PerformanceMetricsFilter,
  PerformanceMetricsState,
  SocialMetricKey,
} from '@/lib/types/performance.types'
import { useErrorStore } from '@/stores/error.stores'

type UsePerformanceChartReturn = {
  data: Ref<PerformanceChartData>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
}

const emptyData = (): PerformanceChartData => ({
  labels: [],
  current: [],
  previous: [],
  previousLabels: [],
  previousRange: null,
})

const previousDateParams = (filter: {
  previousDateFrom?: string
  previousDateTo?: string
}): { previous_date_from: string; previous_date_to: string } | Record<string, never> =>
  filter.previousDateFrom && filter.previousDateTo
    ? { previous_date_from: filter.previousDateFrom, previous_date_to: filter.previousDateTo }
    : {}

export const usePerformanceChart = (
  filterRef: Ref<PerformanceChartFilter>,
  advertiserIdRef: ComputedRef<number | undefined> | Ref<number | undefined>,
): UsePerformanceChartReturn => {
  const data = ref<PerformanceChartData>(emptyData())
  const { loading, error, fetchChart } = useApi({
    fetchChart: async (filter: PerformanceChartFilter, advertiserId: number) => {
      try {
        const params = buildChartParams(filter, advertiserId)
        const metric = filter.metric
        const response = isFinanceMetric(metric)
          ? await financeClient.performanceControllerGetChart({
              ...params,
              ...previousDateParams(filter),
              metric,
              traffic_source: params.traffic_source.toUpperCase() as FinanceChartTrafficSource,
            })
          : await socialMediaClient.chart({ ...params, ...previousDateParams(filter), metric })
        data.value = toPerformanceChartData(response)
      } catch (e) {
        data.value = emptyData()
        throw e
      }
    },
  })

  watch(
    [filterRef, () => advertiserIdRef.value],
    () => {
      const advertiserId = advertiserIdRef.value
      if (advertiserId) {
        void fetchChart(filterRef.value, advertiserId)
      }
    },
    { immediate: true, deep: true },
  )

  return { data, isLoading: loading, error }
}

type MetricsFetcher<K extends PerformanceMetricKey> = (
  filter: PerformanceMetricsFilter,
  keys: K[],
) => Promise<PerformanceMetricRaw[]>

const useMetricsSource = <K extends PerformanceMetricKey>(
  fetcher: MetricsFetcher<K>,
  filter: MaybeRefOrGetter<PerformanceMetricsFilter | undefined>,
  keys: MaybeRefOrGetter<K[]>,
) => {
  const data = ref<PerformanceMetrics>({})
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchMetrics = async () => {
    const currentFilter = toValue(filter)
    const currentKeys = toValue(keys)
    data.value = {}
    error.value = null
    if (!currentFilter || !currentKeys.length) {
      isLoading.value = false
      return
    }
    try {
      isLoading.value = true
      const metrics = await fetcher(currentFilter, currentKeys)
      data.value = normalizePerformanceMetrics(metrics)
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch([() => toValue(filter), () => toValue(keys)], fetchMetrics, { immediate: true, deep: true })

  const metricsState = computed<PerformanceMetricsState>(() =>
    toValue(keys).reduce<PerformanceMetricsState>((acc, key) => {
      acc[key] = { metric: data.value[key], isLoading: isLoading.value, error: error.value }
      return acc
    }, {}),
  )

  return {
    data,
    metricsState,
    isLoading,
    error,
  }
}

export const usePerformanceMetrics = (
  filter: MaybeRefOrGetter<PerformanceMetricsFilter | undefined>,
  keys: MaybeRefOrGetter<SocialMetricKey[]>,
) => {
  return useMetricsSource<SocialMetricKey>(
    async (currentFilter, currentKeys) => {
      const response = await socialMediaClient.summary({
        advertiser_id: currentFilter.advertiserId,
        date_from: currentFilter.dateFrom,
        date_to: currentFilter.dateTo,
        ...previousDateParams(currentFilter),
        traffic_source: currentFilter.trafficSource,
        ...(currentFilter.creatorId && { creator_id: currentFilter.creatorId }),
        metrics: currentKeys,
      })
      return response.metrics
    },
    filter,
    keys,
  )
}

export const useFinanceMetrics = (
  filter: MaybeRefOrGetter<PerformanceMetricsFilter | undefined>,
  keys: MaybeRefOrGetter<FinanceMetricKey[]>,
) => {
  return useMetricsSource<FinanceMetricKey>(
    async (currentFilter, currentKeys) => {
      const response = await financeClient.performanceControllerGetSummary({
        advertiser_id: currentFilter.advertiserId,
        date_from: currentFilter.dateFrom,
        date_to: currentFilter.dateTo,
        ...previousDateParams(currentFilter),
        traffic_source: currentFilter.trafficSource.toUpperCase() as FinanceMetricsTrafficSource,
        ...(currentFilter.creatorId && { creator_id: currentFilter.creatorId }),
        metrics: currentKeys,
      })
      return response.metrics.map((metric) => ({
        ...metric,
        previous: metric.previous ?? null,
      }))
    },
    filter,
    keys,
  )
}

export const useAdvertiserMetrics = (
  filter: MaybeRefOrGetter<PerformanceMetricsFilter | undefined>,
  keys: MaybeRefOrGetter<AdvertiserMetricKey[]>,
) => {
  return useMetricsSource<AdvertiserMetricKey>(
    async (currentFilter, currentKeys) => {
      const response = await metapicApiClient.getUserPerformanceSummary(
        {
          advertiser_id: currentFilter.advertiserId,
          date_from: currentFilter.dateFrom,
          date_to: currentFilter.dateTo,
          ...previousDateParams(currentFilter),
          traffic_source:
            currentFilter.trafficSource.toUpperCase() as GetUserPerformanceSummaryTrafficSource,
        },
        { params: { metrics: currentKeys.join(',') } },
      )
      return response.metrics
    },
    filter,
    keys,
  )
}
